import { foldSheetFit } from '../geometry/fold-sheet-fit';
import { BookFormat } from '../interfaces/book-format.type';
import { pxToMm, resolveSpreadFormatFromPrintSizes } from './book-queries';

/** A width/height pair as any caller hands it over — either side may be missing or not yet parsed. */
export interface FormatDims {
  readonly width?: number | null;
  readonly height?: number | null;
}

/**
 * A stub catalog row and a template whose PSD never parsed both carry 1x1. Classifying those produces a
 * confident wrong answer, so every caller refuses them — which is why the guard lives here rather than
 * being re-decided per callsite.
 */
export function hasClassifiableDims(dims: FormatDims | null | undefined): boolean {
  return !!dims && (dims.width ?? 0) > 1 && (dims.height ?? 0) > 1;
}

/**
 * The pack's format from its basis template. Candidates arrive in basis order — the cover template
 * first, content templates after it — and the first classifiable pair decides. `null` means the pack
 * cannot be classified at all; every caller fails OPEN on it, because a false block costs a user their
 * design while a missed one is caught at generation.
 */
export function resolvePackFormatFromTemplates(candidates: readonly (FormatDims | null | undefined)[]): BookFormat | null {
  const basis = candidates.find(hasClassifiableDims);
  if (!basis) return null;
  return resolveSpreadFormatFromPrintSizes({ width: basis.width as number, height: basis.height as number });
}

/** A PSD template carries no resolution of its own, so every design surface measures it at print DPI. */
const DESIGN_TEMPLATE_DPI = 300;

/**
 * The pack's printed size, «20×30» — ONE page in whole centimetres, read from the same basis candidates
 * in the same order the format is, because a sheet carries `pages` pages side by side (трюмо = 3, aspect alone
 * cannot tell it from a square album spread). `null` means nothing
 * classifies: a pack whose templates never parsed shows no size rather than an invented one.
 */
export function resolvePackSizeLabelFromTemplates(
  candidates: readonly (FormatDims | null | undefined)[],
  pages = 2,
): string | null {
  const basis = candidates.find(hasClassifiableDims);
  if (!basis) return null;
  const cm = (px: number): number => Math.round(pxToMm(px, DESIGN_TEMPLATE_DPI) / 10);
  return `${cm((basis.width as number) / pages)}×${cm(basis.height as number)}`;
}

/** The print product's format from its full-spread dimensions; `null` = unknown, fail OPEN. */
export function resolveProductFormat(dims: FormatDims | null | undefined): BookFormat | null {
  return resolvePackFormatFromTemplates([dims]);
}

/** A pack whose format does not fit the project sheet: both classes, for the refusal text. */
export interface DesignPackSheetConflict {
  readonly packFormat: BookFormat;
  readonly productFormat: BookFormat;
}

/**
 * One size rule of a pack against the project sheet for every gate. A sheet without fold bands compares the format
 * classes of `basis` and the sheet. A трюмо sheet (fold bands) compares no classes — three panels and a square album
 * spread share one aspect — and judges the first classifiable of `folded` (content templates without the cover, or the
 * sheet of the pack's own product) by `foldSheetFit`, the ±10 % rule the pack was approved by. Unknown sides fail OPEN.
 */
export function designPackSheetConflict(input: {
  readonly basis: readonly (FormatDims | null | undefined)[];
  readonly folded: readonly (FormatDims | null | undefined)[];
  readonly sheet: (FormatDims & { readonly foldBands?: unknown }) | null | undefined;
}): DesignPackSheetConflict | null {
  const productFormat: BookFormat | null = resolveProductFormat(input.sheet);
  if (!input.sheet || !productFormat) return null;
  if (!input.sheet.foldBands) {
    const packFormat: BookFormat | null = resolvePackFormatFromTemplates(input.basis);
    return packFormat && packFormat !== productFormat ? { packFormat, productFormat } : null;
  }

  const template = input.folded.find(hasClassifiableDims);
  if (!template) return null;
  const size = { width: template.width as number, height: template.height as number };
  const sheet = { width: input.sheet.width as number, height: input.sheet.height as number };
  return foldSheetFit(size, sheet) ? null : { packFormat: resolveSpreadFormatFromPrintSizes(size), productFormat };
}
